/**
 * Shape + helpers for the notification policy editor.
 *
 * Kept out of the component file so the component module exports only
 * components (React Fast Refresh, and the lint rule that guards it runs
 * at --max-warnings 0).
 *
 * The backend contract lives in apps/sessions/reminder_policy.py. Two
 * parts of it matter here:
 *
 * 1. **Reminder ids must round-trip.** A reminder is identified by its
 *    `id`, not its offset, so that retiming one does not make every
 *    already-reminded beneficiary look un-reminded and get reminded
 *    again. The editor must send back the ids it was given — never
 *    rebuild the list from offsets. `toPayload` exists to make that the
 *    easy path.
 * 2. **Clearing both channels disables that message.** It is the only way
 *    to switch off one of the four event messages, which are not a list
 *    you can delete from. So nothing here may treat email as a floor.
 */

export type Tone = "day_before" | "starting_soon";

export interface Reminder {
  id: string;
  offset_minutes: number;
  tone: Tone;
  email: boolean;
  sms: boolean;
}

export interface ChannelFlags {
  email: boolean;
  sms: boolean;
}

export interface Policy {
  version: number;
  channels: Record<string, ChannelFlags>;
  reminders: Reminder[];
}

export interface Estimate {
  beneficiaries: number;
  emails_per_beneficiary: number;
  sms_per_beneficiary: number;
  emails: number;
  sms: number;
}

export interface PolicyResponse {
  policy: Policy;
  inherited: boolean;
  org_sms_enabled: boolean;
  tones: Tone[];
  event_kinds: string[];
  min_offset_minutes: number;
  estimate?: Estimate;
}

/** The four event-triggered messages, in the order the screen shows them. */
export const EVENT_ORDER = [
  "booking_invite",
  "confirmation",
  "rescheduled",
  "invite",
  // Added 2026-10-01. It had been sending email + SMS with no setting
  // anywhere — invisible on a screen that claims to list every message
  // the platform sends, and unswitchable short of killing SMS entirely.
  "session_started",
] as const;

/**
 * Split an offset into the largest sensible unit.
 *
 * Stored in minutes, but "1440 minutes" is not how anyone thinks about
 * the day before. Only converts when it divides exactly, so 90 minutes
 * stays 90 minutes rather than becoming 1.5 hours.
 */
export function splitOffset(minutes: number): {
  value: number;
  unit: "minutes" | "hours" | "days";
} {
  if (minutes % 1440 === 0) return { value: minutes / 1440, unit: "days" };
  if (minutes % 60 === 0) return { value: minutes / 60, unit: "hours" };
  return { value: minutes, unit: "minutes" };
}

export function toMinutes(value: number, unit: string): number {
  if (unit === "days") return value * 1440;
  if (unit === "hours") return value * 60;
  return value;
}

/** Furthest-out first — the order they fire and the order they read. */
export function sortReminders(reminders: Reminder[]): Reminder[] {
  return [...reminders].sort((a, b) => b.offset_minutes - a.offset_minutes);
}

/**
 * What goes on the wire. **Ids included**, which is the whole point —
 * see the note at the top of this file.
 */
export function toPayload(policy: Policy) {
  return {
    policy: {
      version: policy.version ?? 1,
      channels: policy.channels,
      reminders: policy.reminders.map((r) => ({
        id: r.id,
        offset_minutes: r.offset_minutes,
        tone: r.tone,
        email: r.email,
        sms: r.sms,
      })),
    },
  };
}

/**
 * Client-side echo of the server's estimate, so the readout moves as
 * boxes are ticked instead of waiting for a save.
 *
 * An upper bound, like the server's: a rescheduled notice only fires for
 * somebody who reschedules, and a beneficiary with no mobile never gets
 * an SMS. Reports zero SMS when the org gate is off, because any other
 * number would be a lie.
 */
export function estimate(
  policy: Policy,
  beneficiaries: number,
  orgSmsEnabled: boolean,
): Estimate {
  let emails = 0;
  let sms = 0;
  for (const kind of EVENT_ORDER) {
    const flags = policy.channels[kind];
    if (flags?.email !== false) emails += 1;
    if (flags?.sms) sms += 1;
  }
  for (const r of policy.reminders) {
    if (r.email) emails += 1;
    if (r.sms) sms += 1;
  }
  if (!orgSmsEnabled) sms = 0;
  return {
    beneficiaries,
    emails_per_beneficiary: emails,
    sms_per_beneficiary: sms,
    emails: beneficiaries * emails,
    sms: beneficiaries * sms,
  };
}

/** A local id for a reminder the operator just added. The server
 *  replaces it on save; it only has to be unique in this list. */
export function draftId(): string {
  return `new_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Offsets that would be refused by the server, so the form can say so
 * before a save. Mirrors `normalise` in reminder_policy.py: distinct,
 * and above the floor a reminder could actually be delivered within.
 */
export function offsetProblems(
  reminders: Reminder[],
  floor: number,
): Map<string, "duplicate" | "too_close"> {
  const problems = new Map<string, "duplicate" | "too_close">();
  const seen = new Map<number, string>();
  for (const r of reminders) {
    if (r.offset_minutes < floor) {
      problems.set(r.id, "too_close");
      continue;
    }
    const prior = seen.get(r.offset_minutes);
    if (prior !== undefined) {
      problems.set(r.id, "duplicate");
      problems.set(prior, "duplicate");
    } else {
      seen.set(r.offset_minutes, r.id);
    }
  }
  return problems;
}
